import { useState } from "react";

import { makePostApiCall, makeGetApiCall } from "../../shared/makeApiCall";
import { fetchUsersForDropdown } from "../../shared/usersForDropdown";
import { USER_TYPE_CODE } from "../../shared/userReferenceCodes";
import {
  fetchLocationsForDropdown,
  fetchLocationProductionRoomsForDropdown,
} from "../../shared/locationDropdowns";
import {
  buildUpdateManufacturedProductPayload,
  getManufacturedProductApiErrorMessage,
} from "../../shared/manufacturedProductPayload";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import {
  generateUniqueNumber,
  getInProcessMonitoringStateFromApiResponse,
} from "../../shared/utility";

import NitrateAndPhosphateCalculator from "../../components/forms/production/components/NitrateAndPhosphateCalculator";

export function ManufacturedProductViewModel(logout, productId, addSnackbar) {
  const [loading, setLoading] = useState(true);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [certifiedByOptions, setCertifiedByOptions] = useState([]);
  const [claimsOptions, setClaimsOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [productionRoomOptions, setProductionRoomOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [printLabelDetails, setPrintLabelDetails] = useState({
    production_date: null,
    product_plu: null,
    product_name: null,
    product_description: null,
    product_weight: null,
    lot_number: null,
    supervisor: null,
    product_allergens: null,
    barcodeHtml: null,
    barcode: null,
  });

  const [lotNumbers, setLotNumbers] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState({});
  const [printType, setPrintType] = useState(0);
  const [manufacturedProduct, setManufacturedProduct] = useState(null);
  const [inProcessMonitoringHistory, setInProcessMonitoringHistory] =
    useState(null);

  const getProductReportById = async function (productId) {
    let callResult = await makePostApiCall(
      `manufacturedProducts/id/${productId}`,
      {},
    );

    if (callResult.success) {
      const manufacturedProduct = callResult.data;
      setManufacturedProduct(manufacturedProduct);

      const inProcessState =
        getInProcessMonitoringStateFromApiResponse(manufacturedProduct);
      if (inProcessState) {
        setInProcessMonitoringHistory(inProcessState);
      }

      if (
        manufacturedProduct.recipe_id > 0 &&
        manufacturedProduct.selectedRecipe?.recipe
      ) {
        setSelectedRecipe({
          recipe: manufacturedProduct.selectedRecipe.recipe,
          recipeItems: manufacturedProduct.selectedRecipe.recipeItems,
        });
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(getManufacturedProductApiErrorMessage(callResult));
    }
    setLoading(false);
    return callResult;
  };

  const getCertifiedByOptionsForDropdown = async function () {
    let callResult = await makePostApiCall(`certifiedByOptionsForDropdown`);

    if (callResult.success) {
      setCertifiedByOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    return callResult;
  };

  const getClaimsForDropdown = async function () {
    let callResult = await makePostApiCall(`claimsForDropdown`);

    if (callResult.success) {
      setClaimsOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    return callResult;
  };

  const getLocationsForDropdown = async function () {
    const callResult = await fetchLocationsForDropdown();

    if (callResult.success) {
      setLocationOptions(callResult.data || []);
      return callResult.data || [];
    }

    if (callResult.errorStatus === 401) {
      logout();
    }

    return [];
  };

  const loadProductionRoomsForLocation = async function (location) {
    if (!location?.value) {
      setProductionRoomOptions([]);
      return [];
    }

    const callResult = await fetchLocationProductionRoomsForDropdown(location);

    if (callResult.success) {
      const options = callResult.data || [];
      setProductionRoomOptions(options);
      return options;
    }

    if (callResult.errorStatus === 401) {
      logout();
    }

    setProductionRoomOptions([]);
    return [];
  };

  const getAssemblyItemReceivingByBarcode = async function (search, page) {
    const payload = {
      manufacture: true,
      page: page,
      search: search,
    };
    let callResult = await makePostApiCall(`receivingInvoiceIndex`, payload);

    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const getReceivingItemByBarcode = async function (barcode) {
    let callResult = await makeGetApiCall(`receivingItem/${barcode}`);
    return callResult;
  };

  const getProductsList = async function (search) {
    const payload = {
      page: 1,
      search: search,
    };
    let callResult = await makePostApiCall(`products`, payload);

    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const getReceivingAssemblyItemInStockByName = async function (search) {
    let callResult = await makePostApiCall(
      `getReceivingAssemblyItemInStockByName`,
      {
        search: search,
      },
    );

    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const updateManufacturedProduct = async function (productValues) {
    const payload = buildUpdateManufacturedProductPayload(productValues);
    let updateProductResult = await makePostApiCall(
      "updateManufacturedProduct",
      payload,
    );
    if (updateProductResult.success) {
      if (selectedRecipe.recipe) {
        const recipeIngredientsResult = await makePostApiCall(
          "saveOrUpdateManufactureIngredients",
          {
            ...selectedRecipe,
            manufactured_product_id: payload.manufactured_product_id,
          },
        );
        if (recipeIngredientsResult.success) {
          await getProductReportById(payload.manufactured_product_id);
        } else {
          if (recipeIngredientsResult.errorStatus === 401) {
            logout();
          }
          showErrorModal(
            getManufacturedProductApiErrorMessage(recipeIngredientsResult),
          );
        }
      }
      return updateProductResult;
    } else {
      if (updateProductResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        getManufacturedProductApiErrorMessage(updateProductResult),
      );
    }
  };

  const getManufacturedProductIngredients = async function (
    manufactedProductId,
  ) {
    let callResult = await makePostApiCall(
      `getManufacturedProductIngredients/${manufactedProductId}`,
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const addIngredientToManufacturedProduct = async function (
    ingredientValues,
    isEdit,
  ) {
    let callResult = await makePostApiCall(
      isEdit
        ? "updateManufacturedProductIngredientById/" + ingredientValues.id
        : "addIngredientToManufacturedProduct",
      ingredientValues,
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const deleteIngredientFromManufacturedProduct = async function (
    ingredientId,
  ) {
    let callResult = await makePostApiCall(
      `deleteIngredientFromManufacturedProduct/${ingredientId}`,
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
    return callResult;
  };

  const addManualEntryForManufacturingProduct = async function (productValues) {
    let callResult = await makePostApiCall(
      `addManualEntryToManufacturedProduct`,
      productValues,
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const getManufacturedProductLabel = async function (manufacturedProductId) {
    let callResult = await makePostApiCall(
      `getManufacturedProductLabel/${manufacturedProductId}`,
    );
    if (callResult.success) {
      setPrintType(1);
      setPrintLabelDetails(callResult.data);

      await new Promise((resolve) => setTimeout(resolve, 1500));
      window.print();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const getUsersForDropdown = async function (type, department) {
    let callResult = await fetchUsersForDropdown(type, department);

    if (callResult.success) {
      if (type === USER_TYPE_CODE.REGULAR) {
        setEmployeeOptions(callResult.data);
      } else {
        setSupervisorOptions(callResult.data);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
    return callResult;
  };

  const fetchRecipesForDropdown = async function () {
    let callResult = await makePostApiCall(`recipesForDropdown`);

    if (callResult.success) {
      setRecipes(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
    return callResult;
  };

  const fetchLotNumbersForDropdown = async function () {
    let callResult = await makeGetApiCall(`lotsForDropdown`);

    if (callResult.success) {
      setLotNumbers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const fetchRecipeItems = async function (id, batchAmount) {
    let callResult = await makePostApiCall(`fetchRecipeForUse/${id}`);

    if (callResult.success) {
      const newRecipe = {
        recipe: callResult.data.recipe,
        recipeItems: [
          ...callResult.data.recipeItems,
          ...callResult.data.packagingItems,
        ],
      };

      calculateAmountRequiredForRecipeItems(batchAmount, newRecipe);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const printProductionCostReport = async function () {
    setPrintType(2);

    await new Promise((resolve) => setTimeout(resolve, 1500));
    window.print();
  };

  const getProductionBatchReport = async function (id) {
    let callResult = await makePostApiCall(`createProductionBatchReport/${id}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getLabelCheckReport = async function (id) {
    let callResult = await makePostApiCall(`createLabelCheckReport/${id}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const getVacuumPackReport = async function (id) {
    let callResult = await makePostApiCall(`createVacuumPackReport/${id}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const saveScannedItemsToProduction = async function (
    scannedItems,
    lotNumber,
  ) {
    var payload = {
      scanned_items: scannedItems,
      lot_number: lotNumber,
    };
    let callResult = await makePostApiCall(
      `saveScannedItemsToProduction`,
      payload,
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message
            ? callResult.data.message
            : "Scanned items saved to production",
          SnackbarType.Success,
        ),
      );
      return callResult;
    } else {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message
            ? callResult.data.message
            : "Failed to save scanned items",
          SnackbarType.Error,
        ),
      );
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCookingAndCoolingReport = async function (id) {
    let callResult = await makePostApiCall(
      `createCookingAndCoolingReport/${id}`,
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const getMetalDetectorReport = async function (id) {
    let callResult = await makePostApiCall(`createMetalDetectorReport/${id}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const onGenerateNitrateAndPhosphateReport = async function (payload) {
    let callResult = await makePostApiCall(`createNitrateAndPhosphateReport`, {
      ...payload,
      product_name: manufacturedProduct.product_name,
    });

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Something went wrong, please try again later.");
    }
  };

  const openNitrateAndPhosphateCalculator = () => {
    setEmptyModelOpen({
      childComponent: (
        <NitrateAndPhosphateCalculator
          manufacturedProduct={manufacturedProduct}
          actionCall={onGenerateNitrateAndPhosphateReport}
        />
      ),
      open: true,
    });
  };

  function calculateAmountRequiredForRecipeItems(
    batchAmount,
    recipe = selectedRecipe,
  ) {
    console.log("batchAmount", batchAmount);
    const updatedRecipeItems = recipe.recipeItems.map((item) => ({
      ...item,
      amount_required: (
        parseFloat(batchAmount) * parseFloat(item.recipe_per_kg)
      ).toFixed(2),
    }));
    setSelectedRecipe({ ...recipe, recipeItems: updatedRecipeItems });
  }

  const updateInProcessMonitoringHistory = async function (payload) {
    let callResult = await makePostApiCall(
      "inProcessHistoryOnNextStatus",
      payload,
    );
    if (callResult.success) {
      const statusRecord = getInProcessMonitoringStateFromApiResponse(
        callResult.data,
      );
      if (statusRecord) {
        setInProcessMonitoringHistory(statusRecord);
      }
      addSnackbar(
        new SnackbarData(
          Date.now().toString(),
          callResult.data?.message ||
            (statusRecord?.in_process_status_name
              ? `Product status moved to ${statusRecord.in_process_status_name}`
              : callResult.data?.next_process_status_name
                ? `Product status moved to ${callResult.data.next_process_status_name}`
                : "Status updated"),
          SnackbarType.Success,
        ),
      );
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        addSnackbar(
          new SnackbarData(
            Date.now().toString(),
            "Failed to update status",
            SnackbarType.Error,
          ),
        );
      }
    }
  };

  const previousInProcessMonitoringHistory = async function (payload) {
    let callResult = await makePostApiCall(
      "inProcessHistoryOnPreviousStatus",
      payload,
    );
    if (callResult.success) {
      const statusRecord = getInProcessMonitoringStateFromApiResponse(
        callResult.data,
      );
      if (statusRecord) {
        setInProcessMonitoringHistory(statusRecord);
      }
      addSnackbar(
        new SnackbarData(
          Date.now().toString(),
          callResult.data?.message ||
            (statusRecord?.in_process_status_name
              ? `Product status moved to ${statusRecord.in_process_status_name}`
              : callResult.data?.previous_process_status_name
                ? `Product status moved to ${callResult.data.previous_process_status_name}`
                : "Status updated"),
          SnackbarType.Success,
        ),
      );
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        addSnackbar(
          new SnackbarData(
            Date.now().toString(),
            "Failed to update status",
            SnackbarType.Error,
          ),
        );
      }
    }
  };

  return {
    loading,
    addIngredientToManufacturedProduct,
    deleteIngredientFromManufacturedProduct,
    emptyModal,
    getManufacturedProductIngredients,
    getProductReportById,
    manufacturedProduct,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    certifiedByOptions,
    getAssemblyItemReceivingByBarcode,
    getReceivingItemByBarcode,
    getCertifiedByOptionsForDropdown,
    getProductsList,
    updateManufacturedProduct,
    getReceivingAssemblyItemInStockByName,
    addManualEntryForManufacturingProduct,
    getManufacturedProductLabel,
    printLabelDetails,
    employeeOptions,
    getUsersForDropdown,
    fetchRecipesForDropdown,
    recipes,
    fetchRecipeItems,
    selectedRecipe,
    setSelectedRecipe,
    showErrorModal,
    printType,
    printProductionCostReport,
    getProductionBatchReport,
    getLabelCheckReport,
    getVacuumPackReport,
    getCookingAndCoolingReport,
    getMetalDetectorReport,
    claimsOptions,
    getClaimsForDropdown,
    supervisorOptions,
    locationOptions,
    productionRoomOptions,
    getLocationsForDropdown,
    loadProductionRoomsForLocation,
    openNitrateAndPhosphateCalculator,
    calculateAmountRequiredForRecipeItems,
    fetchLotNumbersForDropdown,
    lotNumbers,
    saveScannedItemsToProduction,
    inProcessMonitoringHistory,
    updateInProcessMonitoringHistory,
    previousInProcessMonitoringHistory,
  };
}
